import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../utils/firebase';
import Logo from '../components/Logo';
import { getAchievementInfo } from '../utils/achievements';
import Header from '../components/Header';
import BadgeDisplay from '../components/BadgeDisplay';

const PublicTeamProfile = () => {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadTeam = async () => {
      try {
        // Find team by league ID
        const teamQuery = query(collection(db, 'teams'), where('leagueId', '==', leagueId.toUpperCase()));
        const teamSnapshot = await getDocs(teamQuery);
        
        if (teamSnapshot.empty) {
          setError('Team not found');
          setLoading(false);
          return;
        }

        const teamDoc = teamSnapshot.docs[0];
        setTeam({ id: teamDoc.id, ...teamDoc.data() });
      } catch (error) {
        console.error('Error loading team:', error);
        setError('Failed to load team');
      } finally {
        setLoading(false);
      }
    };

    if (leagueId) {
      loadTeam();
    }
  }, [leagueId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading team profile...</div>
      </div>
    );
  }

  if (error || !team) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Logo className="h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-4">Team Not Found</h1>
          <p className="text-gray-400 mb-6">The team you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/team')}
            className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
          >
            Back to Team Platform
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <Logo className="h-16 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-white mb-2">{team.name}</h1>
            <p className="text-gray-400">League ID: {team.leagueId}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Team Stats */}
            <div className="lg:col-span-2 space-y-6">
              {/* Team Profile Picture */}
              <div className="bg-gray-800 rounded-lg p-6">
                <h2 className="text-xl font-bold text-white mb-4">Team Profile</h2>
                <div className="flex items-center space-x-6">
                  <div className="flex-shrink-0">
                    {team.profileImageUrl ? (
                      <img
                        src={team.profileImageUrl}
                        alt={`${team.name} profile`}
                        className="w-24 h-24 rounded-full object-cover border-2 border-gray-600"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-gray-700 border-2 border-gray-600 flex items-center justify-center">
                        <span className="text-gray-400 text-2xl">🏆</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-gray-300">{team.description || 'No description available.'}</p>
                    <div className="flex items-center space-x-2 mt-2">
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: team.colors?.primary || '#10B981' }}></div>
                      <div className="w-4 h-4 rounded" style={{ backgroundColor: team.colors?.secondary || '#1F2937' }}></div>
                      <span className="text-gray-400 text-sm">Team Colors</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* GPA and Ranking */}
              <div className="bg-gray-800 rounded-lg p-6">
                <h2 className="text-xl font-bold text-white mb-4">Team Performance</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-500">{team.gpa?.toFixed(2) || '0.00'}</div>
                    <div className="text-gray-400 text-sm">GPA</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-500">#{team.ranking || 'N/A'}</div>
                    <div className="text-gray-400 text-sm">Ranking</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-500">{team.gamesPlayed || 0}</div>
                    <div className="text-gray-400 text-sm">Games</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-500">{team.totalPoints || 0}</div>
                    <div className="text-gray-400 text-sm">Points</div>
                  </div>
                </div>
              </div>

              {/* Team Members (Public View) */}
              <div className="bg-gray-800 rounded-lg p-6">
                <h2 className="text-xl font-bold text-white mb-4">Team Members ({team.members?.length || 0}/{team.maxMembers || 10})</h2>
                <div className="space-y-3">
                  {team.memberDetails && Object.entries(team.memberDetails).map(([memberId, member]) => (
                    <div key={memberId} className="flex items-center space-x-4 p-3 bg-gray-700 rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-white font-semibold">
                            {memberId === team.captain ? '👑 ' : ''}
                            {member.name || 'Unnamed Member'}
                          </span>
                          {memberId === team.captain && (
                            <span className="text-green-500 text-sm">(Captain)</span>
                          )}
                        </div>
                        {member.category && (
                          <div className="text-gray-400 text-sm">Strongest: {member.category}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Achievements */}
              <BadgeDisplay 
                achievements={team?.achievements}
                getAchievementInfo={getAchievementInfo}
                title="Team Achievements"
                emptyMessage="No achievements yet. Keep playing to earn badges!"
              />
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Challenge Button */}
              <div className="bg-gray-800 rounded-lg p-6">
                <h2 className="text-xl font-bold text-white mb-4">Challenge This Team</h2>
                <p className="text-gray-400 text-sm mb-4">Ready to compete? Join a team and challenge them!</p>
                <button 
                  onClick={() => navigate('/team')}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
                >
                  Join Team Platform
                </button>
              </div>

              {/* Team Info */}
              <div className="bg-gray-800 rounded-lg p-6">
                <h2 className="text-xl font-bold text-white mb-4">Team Info</h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Created:</span>
                    <span className="text-white">
                      {team.createdAt ? new Date(team.createdAt.toDate()).toLocaleDateString() : 'Unknown'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">League ID:</span>
                    <span className="text-white font-mono">{team.leagueId}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicTeamProfile; 